import React, { useMemo } from "react";
import { GeoJSON, Popup, Marker } from "react-leaflet";
import L from "leaflet";

// NWS event-category colors. Tuned to be visible on both the dark and
// satellite basemaps without overpowering active-fire markers.
function styleForEvent(event) {
  const e = (event || "").toLowerCase();
  if (e.includes("red flag") || e.includes("fire weather"))
    return { color: "#0A0D0C", fillColor: "#FF3B1F", weight: 1.5, fillOpacity: 0.18, dashArray: "4 2" };
  if (e.includes("tornado") || e.includes("severe thunderstorm"))
    return { color: "#A855F7", fillColor: "#A855F7", weight: 1.5, fillOpacity: 0.15 };
  if (e.includes("excessive heat") || e.includes("heat"))
    return { color: "#F97316", fillColor: "#F97316", weight: 1.5, fillOpacity: 0.12 };
  if (e.includes("flood") || e.includes("flash"))
    return { color: "#38BDF8", fillColor: "#38BDF8", weight: 1.5, fillOpacity: 0.15 };
  if (e.includes("wind") || e.includes("high wind"))
    return { color: "#FACC15", fillColor: "#FACC15", weight: 1.5, fillOpacity: 0.12 };
  if (e.includes("winter") || e.includes("snow") || e.includes("ice") || e.includes("freeze"))
    return { color: "#93C5FD", fillColor: "#93C5FD", weight: 1.2, fillOpacity: 0.1 };
  // Catch-all advisory style
  return { color: "#FBBF24", fillColor: "#FBBF24", weight: 1.2, fillOpacity: 0.1 };
}

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtParamKey(k) {
  // "maxWindGust" -> "Max Wind Gust"
  return k
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

export default function WeatherAlertsLayer({ alerts = [] }) {
  // Build a stable GeoJSON FeatureCollection of only alerts with geometry.
  const features = useMemo(() => {
    const out = [];
    for (const a of alerts) {
      if (!a.geometry) continue;
      out.push({
        id: a.id,
        event: a.event,
        headline: a.headline,
        severity: a.severity,
        affected_areas: a.area_desc,
        zones: a.zones,
        zone_labels: a.zone_labels,
        effective: a.onset || a.sent,
        ends: a.ends || a.expires,
        geometry: a.geometry,
        parameters: a.parameters,
      });
    }
    // Largest polygons first so they render underneath — smaller, more
    // specific alerts (e.g. Red Flag zones) stay clickable above the huge
    // statewide ones (e.g. Air Quality Alerts).
    const area = (g) => {
      let minx = 999, maxx = -999, miny = 999, maxy = -999;
      const walk = (c) => {
        if (!c || !c.length) return;
        if (typeof c[0] === "number") {
          if (c[0] < minx) minx = c[0];
          if (c[0] > maxx) maxx = c[0];
          if (c[1] < miny) miny = c[1];
          if (c[1] > maxy) maxy = c[1];
        } else c.forEach(walk);
      };
      walk(g.coordinates || (g.geometries || []).map((x) => x.coordinates));
      return (maxx - minx) * (maxy - miny);
    };
    out.sort((a, b) => area(b.geometry) - area(a.geometry));
    return out;
  }, [alerts]);

  return features.map((f) => {
    const style = styleForEvent(f.event);
    const gj = { type: "Feature", geometry: f.geometry, properties: {} };
    return (
      <React.Fragment key={`wx-${f.id}`}>
      <GeoJSON data={gj} style={() => style}>
        <Popup>
          <div className="min-w-[240px]" data-testid={`wx-alert-popup-${f.id}`}>
            <div
              className="text-[10px] font-bold uppercase tracking-wider mb-1"
              style={{ color: style.fillColor }}
            >
              NWS · {f.severity || "Alert"}
            </div>
            <div className="font-display font-bold text-sm text-tac-text mb-1 leading-snug">
              {f.event}
            </div>
            {f.headline && (
              <div className="text-[12px] text-tac-text mb-1 leading-snug">
                {f.headline}
              </div>
            )}
            <div className="text-[11px] text-tac-dim font-mono-tac tabular space-y-0.5">
              {f.effective && <div>Issued {fmtDateTime(f.effective)}</div>}
              {f.ends && <div>Through {fmtDateTime(f.ends)}</div>}
              {f.affected_areas && (
                <div className="text-tac-faint mt-1 line-clamp-3">
                  {f.affected_areas}
                </div>
              )}
              {f.zones?.length > 0 && (
                <div className="text-tac-dim mt-0.5" data-testid={`wx-popup-zones-${f.id}`}>
                  Zone{f.zones.length === 1 ? "" : "s"}: {f.zones.join(" · ")}
                </div>
              )}
            </div>
            {f.parameters && Object.keys(f.parameters).length > 0 && (
              <div
                className="mt-2 pt-1.5 border-t border-tac-border/60 space-y-0.5"
                data-testid={`wx-alert-params-${f.id}`}
              >
                {Object.entries(f.parameters).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-[11px] font-mono-tac">
                    <span className="text-tac-faint uppercase tracking-wide shrink-0">
                      {fmtParamKey(k)}
                    </span>
                    <span className="text-tac-text ml-auto text-right">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: NWS / api.weather.gov
            </div>
          </div>
        </Popup>
      </GeoJSON>
      {(f.zone_labels || []).map((z) => (
        <Marker
          key={`wxz-${f.id}-${z.code}`}
          position={[z.lat, z.lon]}
          interactive={false}
          icon={L.divIcon({
            className: "",
            html: `<div style="display:inline-block;transform:translate(-50%,-50%);padding:1px 5px;font:700 10px/1.4 'JetBrains Mono',monospace;letter-spacing:0.05em;color:#FFFFFF;background:rgba(10,13,12,0.72);border:1px solid ${style.fillColor}55;border-radius:2px;white-space:nowrap;pointer-events:none;">${z.code}</div>`,
            iconSize: null,
          })}
        />
      ))}
      </React.Fragment>
    );
  });
}
